import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { login as loginRequest, guestLogin, UserType } from "../api/authService";
import { getExpectedUserType } from "../utils/authScope";
import { getOrCreateDeviceId } from "../utils/deviceId";

const AuthContext = createContext(null);

const STORAGE_KEY = "bigmarket_auth";
const TOKEN_KEY = "bigmarket_token";

function readStoredAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Writes synchronously so axiosClient's interceptor (which reads localStorage
// directly) sees the token immediately, without waiting for the effect below
// to run on the next render.
function persistAuth(data) {
  try {
    if (data) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(TOKEN_KEY, data.token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

export function AuthProvider({ children }) {
  const [storedUser, setUser] = useState(readStoredAuth);
  const [loading, setLoading] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    persistAuth(storedUser);
  }, [storedUser]);

  const expectedUserType = getExpectedUserType(location.pathname);
  const user = storedUser && storedUser.userType === expectedUserType ? storedUser : null;

  const ensuringRef = useRef(null);

  // Lazily creates (or reuses) a guest account for anonymous visitors on the
  // client-facing site, mirroring the mobile app's device-id-based guest
  // login - called on demand by actions like add-to-cart/wishlist/follow,
  // not on every page load.
  const ensureSession = useCallback(async () => {
    if (user) return user;
    if (expectedUserType !== UserType.User) return null;
    if (!ensuringRef.current) {
      ensuringRef.current = (async () => {
        const deviceId = getOrCreateDeviceId();
        const data = await guestLogin(deviceId);
        persistAuth(data);
        setUser(data);
        return data;
      })().finally(() => {
        ensuringRef.current = null;
      });
    }
    return ensuringRef.current;
  }, [user, expectedUserType]);

  // Automatic guest registration on first visit, like the mobile app's launch-time guest login.
  // Skipped whenever *any* session is stored (even another role's): a guest login would overwrite
  // it, since only one role's session is kept at a time.
  const registerGuestIfNone = useCallback(async () => {
    if (readStoredAuth()) return null;
    return ensureSession();
  }, [ensureSession]);

  async function login(email, password) {
    setLoading(true);
    try {
      const data = await loginRequest(email, password);
      setUser(data);
      return data;
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setUser(null);
  }

  // For flows (OTP verify, dev-login) where the auth payload is already fetched
  // and just needs to be stored, unlike `login` which performs the request itself.
  function setSession(data) {
    setUser(data);
  }

  function updateUser(partialUser) {
    setUser((prev) => (prev ? { ...prev, ...partialUser } : prev));
  }

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    ensureSession,
    registerGuestIfNone,
    setSession,
    updateUser,
    loginModalOpen,
    openLoginModal: () => setLoginModalOpen(true),
    closeLoginModal: () => setLoginModalOpen(false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
